import React, { useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import Flatpickr from "react-flatpickr";
import { Link } from "react-router-dom";
import { Dropdown } from "Common/Components/Dropdown";

// Icon
import {
  MoreHorizontal,
  Eye,
  FileEdit,
  Trash2,
  Search,
  Plus,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import {
  getJewelleries as onGetJewelleries,
  deleteJewellery as onDeleteJewellery,
} from "slices/thunk";

import TableContainer from "Common/TableContainer";
import DeleteModal from "Common/DeleteModal";

// react-redux
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";

import {
  getProductList as onGetProductList,
  deleteProductList as onDeleteProductList,
} from "slices/thunk";
import { ToastContainer } from "react-toastify";
import filterDataBySearch from "Common/filterDataBySearch";
import {
  PriceResponse,
  useJewelleryContext,
} from "context/JwelleryPriceContext";
import JewelleryPrice from "./Components/jewelleryPrice";

const ProductListView = () => {
  const dispatch = useDispatch<any>();

  const selectDataList = createSelector(
    (state: any) => state.Jewellery,
    (state) => ({
      jewelleries: state.jewelleries,
      totalCount: state.totalCount,
    })
  );

  const { jewelleries, totalCount } = useSelector(selectDataList);

  const [data, setData] = useState<any>([]);
  const [eventData, setEventData] = useState<any>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [diamondPrices, setDiamondPrices] = useState<PriceResponse[]>([]);

  const { handleJewelleryPrice, getDiamondPrices } = useJewelleryContext();

  // Get Data
  useEffect(() => {
    dispatch(onGetProductList());
  }, [dispatch]);

  useEffect(() => {
    setData(jewelleries);
  }, [jewelleries]);

  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  //   const getPages = () => Array.from({ length: totalPages }, (_, i) => i + 1);
  const getPages = () => {
    if (totalPages <= 5)
      return Array.from({ length: totalPages }, (_, i) => i + 1);

    const pages: (number | "...")[] = [1]; // Always show the first page

    if (currentPage > 3) pages.push("...");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 2) pages.push("...");

    pages.push(totalPages); // Always show the last page

    return pages;
  };

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  // Delete Data
  const onClickDelete = (cell: any) => {
    setDeleteModal(true);
    if (cell.id) {
      setEventData(cell);
    }
  };

  const handleDelete = () => {
    if (eventData) {
      dispatch(onDeleteProductList(eventData.id));
      setDeleteModal(false);
    }
  };

  // Search Data
  const filterSearchData = (e: any) => {
    const search = e.target.value;
    const keysToSearch = ["productCode", "productName", "category", "status"];
    filterDataBySearch(jewelleries, search, keysToSearch, setData);
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const payload = {
      page: currentPage,
      pageSize,
    };
    dispatch(onGetJewelleries(payload));
  }, [dispatch, currentPage]);

  const columns = useMemo(
    () => [
      {
        header: "SKU",
        accessorKey: "sku",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <Link
            to="#"
            className="transition-all duration-150 ease-linear product_code text-custom-500 hover:text-custom-600"
          >
            <div className="max-w-[150px] text-ellipsis whitespace-nowrap overflow-hidden">
              {cell.getValue()}
            </div>
          </Link>
        ),
      },
      {
        header: "Name   ",
        accessorKey: "title",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => (
          <Link
            to="/apps-ecommerce-product-overview"
            className="flex items-center gap-2"
          >
            <h6 className="product_name max-w-[150px] text-ellipsis whitespace-nowrap overflow-hidden">
              {cell.getValue()}
            </h6>
          </Link>
        ),
      },
      {
        header: "Category",
        accessorKey: "category",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <span className="category px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-slate-100 border-slate-200 text-slate-500 dark:bg-slate-500/20 dark:border-slate-500/20 dark:text-zink-200">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "Sub Category",
        accessorKey: "subcategory",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <span className="category px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-slate-100 border-slate-200 text-slate-500 dark:bg-slate-500/20 dark:border-slate-500/20 dark:text-zink-200">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "Shape",
        accessorKey: "shape",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Metal Price",
        accessorKey: "metalPrice",
        enableColumnFilter: false,
        cell: ({ row }: any) => {
          const jewellery = row.original;
          const metals = ["yellowgold", "rosegold", "whitegold"] as const;
          const karats = ["14k", "18k"] as const;

          return (
            <div className="space-y-1">
              {metals.map((metal) =>
                karats.map((karat, index) => {
                  const price: any = handleJewelleryPrice(
                    jewellery,
                    metal,
                    karat,
                    // diamondPrices,
                    1,
                    "separate"
                  );
                  console.log(price);
                  
                  return (
                    <div key={index}>
                      <span className="font-medium capitalize category px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-slate-100 border-slate-200 text-slate-500 dark:bg-slate-500/20 dark:border-slate-500/20 dark:text-zink-200">
                        {metal} {karat}:
                      </span>{" "}
                      $ {price?.metalPrice}
                      <br />
                    </div>  
                  );
                })
              )}
            </div>
          );
        },
      },
      {
        header: "Diamond Price",
        accessorKey: "diamondPrice",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const jewellery = cell.row.original; // Get the jewellery object
          return (
            <>
              <JewelleryPrice jewellery={jewellery} type="diamond" />
            </>
          );
        },
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => (
          <Dropdown className="relative dropdown">
            <Dropdown.Trigger
              className="flex items-center justify-center size-[30px] dropdown-toggle p-0 text-slate-500 btn bg-slate-100 hover:text-white hover:bg-slate-600 focus:text-white focus:bg-slate-600 focus:ring focus:ring-slate-100 active:text-white active:bg-slate-600 active:ring active:ring-slate-100 dark:bg-slate-500/20 dark:text-slate-400 dark:hover:bg-slate-500 dark:hover:text-white dark:focus:bg-slate-500 dark:focus:text-white dark:active:bg-slate-500 dark:active:text-white dark:ring-slate-400/20"
              id="productAction1"
              data-bs-toggle="dropdown"
            >
              <MoreHorizontal className="size-3" />
            </Dropdown.Trigger>
            <Dropdown.Content
              placement={cell.row.index ? "top-end" : "right-end"}
              className="absolute z-50 py-2 mt-1 ltr:text-left rtl:text-right list-none bg-white rounded-md shadow-md dropdown-menu min-w-[10rem] dark:bg-zink-600"
              aria-labelledby="productAction1"
            >
              <li>
                <Link
                  className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200"
                  to="/apps-ecommerce-product-overview"
                >
                  <Eye className="inline-block size-3 ltr:mr-1 rtl:ml-1" />{" "}
                  <span className="align-middle">Overview</span>
                </Link>
              </li>
              <li>
                <Link
                  className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200"
                  to="/apps-ecommerce-product-create"
                >
                  <FileEdit className="inline-block size-3 ltr:mr-1 rtl:ml-1" />{" "}
                  <span className="align-middle">Edit</span>
                </Link>
              </li>
              <li>
                <Link
                  className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200"
                  to="#!"
                  onClick={() => {
                    const data = cell.row.original;
                    onClickDelete(data);
                  }}
                >
                  <Trash2 className="inline-block size-3 ltr:mr-1 rtl:ml-1" />{" "}
                  <span className="align-middle">Delete</span>
                </Link>
              </li>
            </Dropdown.Content>
          </Dropdown>
        ),
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <BreadCrumb title="List View" pageTitle="Products" />
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={handleDelete}
      />
      <ToastContainer closeButton={false} limit={1} />
      <div className="card" id="productListTable">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-12">
            <div className="xl:col-span-3">
              <div className="relative">
                <input
                  type="text"
                  className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Search for ..."
                  autoComplete="off"
                  onChange={(e) => filterSearchData(e)}
                />
                <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
              </div>
            </div>
            <div className="xl:col-span-2">
              <div>
                <Flatpickr
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  options={{
                    dateFormat: "d M, Y",
                    mode: "range",
                  }}
                  placeholder="Select date"
                  readOnly={true}
                />
              </div>
            </div>
            <div className="lg:col-span-2 ltr:lg:text-right rtl:lg:text-left xl:col-span-2 xl:col-start-11">
              <Link
                to="/add-jewellery"
                type="button"
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              >
                <Plus className="inline-block size-4" />{" "}
                <span className="align-middle">Add Product</span>
              </Link>
            </div>
          </div>
        </div>
        <div className="!pt-1 card-body">
          {data && data.length > 0 ? (
            <TableContainer
              isPagination={true}
              columns={columns || []}
              data={data || []}
              customPageSize={7}
              divclassName="overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
              thclassName="px-3.5 py-2.5 font-semibold border-b border-slate-200 dark:border-zink-500"
              tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
              PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
            />
          ) : (
            <div className="noresult">
              <div className="py-6 text-center">
                <Search className="size-6 mx-auto mb-3 text-sky-500 fill-sky-100 dark:fill-sky-500/20" />
                <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                <p className="mb-0 text-slate-500 dark:text-zink-200">
                  We've searched more than 199+ product We did not find any
                  product for you search.
                </p>
              </div>
            </div>
          )}
          <ul className="flex justify-end pt-8 flex-wrap items-center gap-2 shrink-0">
            <li>
              <button
                onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`inline-flex items-center justify-center bg-white dark:bg-zink-700 h-8 px-3 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zink-500 ${
                  currentPage !== 1 ? "hover:text-blue-500" : "text-gray-300"
                }`}
              >
                <ChevronLeft />
              </button>
            </li>
            {getPages().map((page) => (
              <li key={page}>
                {page === "..." ? (
                  <span className="inline-flex items-center justify-center h-8 px-3 text-gray-400">
                    ...
                  </span>
                ) : (
                  <button
                    onClick={() => onPageChange(page as number)}
                    className={`inline-flex items-center justify-center bg-white dark:bg-zink-700 h-8 px-3 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zink-500 text-slate-500 dark:text-zink-200 ${
                      currentPage === page
                        ? "bg-blue-500 text-blue-500"
                        : "hover:bg-gray-200"
                    }`}
                  >
                    {page}
                  </button>
                )}
              </li>
            ))}
            {/* Next Button */}
            <li>
              <button
                onClick={() =>
                  currentPage < totalPages && onPageChange(currentPage + 1)
                }
                disabled={currentPage === totalPages}
                className={`inline-flex items-center justify-center bg-white dark:bg-zink-700 h-8 px-3 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zink-500 ${
                  currentPage !== totalPages
                    ? "hover:text-blue-500"
                    : "text-gray-300"
                }`}
              >
                <ChevronRight />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ProductListView;
